import React from "react";
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ArrowLeft, 
  Heart, 
  LayoutDashboard, 
  TrendingUp, 
  Rocket, 
  Loader2,
  Zap,
  Clock,
  Target,
  DollarSign,
  CheckCircle2,
  Users,
  Shield,
  Sparkles,
  BarChart3,
  Lightbulb
} from "lucide-react";
import { useCreateProjectFromStartup } from "@/hooks/useCreateProjectFromStartup";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

interface TrendData {
  date: string;
  popularity_index: number;
}

interface DeepDescription {
  problem: string;
  solution: string;
  target_audience: string;
  timing: string;
  competition: string;
  risks: string;
}

interface BuildPhase {
  phase_number: number;
  phase_name: string;
  description: string;
  steps: Array<{
    title: string;
    description: string;
  }>;
}

interface BuildSteps {
  phases: BuildPhase[];
}

interface Idea {
  id: string;
  title: string;
  tagline: string | null;
  description: string | null;
  tags: string[] | null;
  user_id: string;
  deep_description?: DeepDescription | null;
  build_steps?: BuildSteps | null;
  score?: number;
  opportunity_score?: number;
  feasibility_score?: number;
  revenue_potential_score?: number;
}

// Calculate derived metrics from idea data
const getIdeaMetrics = (idea: Idea) => {
  const baseScore = idea.score || idea.opportunity_score || 72;
  const ideaScore = Math.min(100, Math.max(0, Math.round(baseScore)));
  
  // Derive difficulty from feasibility (inverse relationship)
  const feasibility = idea.feasibility_score || 65;
  const difficulty = feasibility >= 80 ? "Easy" : feasibility >= 60 ? "Medium" : "Hard";
  
  // Derive time to MVP from build_steps phases count or default
  const phases = idea.build_steps?.phases?.length || 3;
  const timeToMVP = phases <= 2 ? "2-3 weeks" : phases <= 4 ? "4-6 weeks" : "8-12 weeks";
  
  // Derive monetization strength from revenue_potential_score
  const revPotential = idea.revenue_potential_score || 70;
  const monetization = revPotential >= 80 ? "Strong" : revPotential >= 60 ? "Good" : "Moderate";
  
  return { ideaScore, difficulty, timeToMVP, monetization, revPotential, feasibility };
};

// Generate "Why This Wins" bullets from idea data
const getWhyThisWins = (idea: Idea): string[] => {
  const bullets: string[] = [];
  
  if (idea.deep_description?.timing) {
    bullets.push(idea.deep_description.timing.split('.')[0] + ".");
  } else {
    bullets.push("Market timing is optimal with growing demand in this space.");
  }
  
  if (idea.deep_description?.target_audience) {
    const audience = idea.deep_description.target_audience.split('.')[0];
    bullets.push(`Clear target: ${audience}.`);
  } else {
    bullets.push("Well-defined target audience with proven willingness to pay.");
  }
  
  if (idea.deep_description?.solution) {
    const solution = idea.deep_description.solution.split('.')[0];
    bullets.push(`Unique approach: ${solution}.`);
  } else {
    bullets.push("Differentiated solution with clear competitive advantages.");
  }
  
  return bullets.slice(0, 3);
};

const StartupDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createProjectFromIdea, isCreating, progress } = useCreateProjectFromStartup();
  const [idea, setIdea] = useState<Idea | null>(null);
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (id) {
      fetchIdea();
      fetchTrendData();
      trackView();
    }
  }, [id, user]);

  const fetchIdea = async () => {
    try {
      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      
      const ideaData: Idea = {
        ...data,
        deep_description: data.deep_description as unknown as DeepDescription | null,
        build_steps: data.build_steps as unknown as BuildSteps | null,
      };
      
      setIdea(ideaData);
      setIsOwner(user?.id === data.user_id);
      
      if (user && user.id !== data.user_id) {
        const { data: existing } = await supabase
          .from("ideas")
          .select("id")
          .eq("user_id", user.id)
          .eq("title", data.title)
          .maybeSingle();
        setIsSaved(!!existing);
      }
    } catch (error) {
      console.error("Error fetching idea:", error);
      toast({
        title: "Error",
        description: "Failed to load startup details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendData = async () => {
    try {
      const { data, error } = await supabase.rpc("get_idea_trend_data", {
        idea_id_param: id,
        days_back: 90,
      });

      if (error) throw error;
      setTrendData(data || []);
    } catch (error) {
      console.error("Error fetching trend data:", error);
    }
  };

  const trackView = async () => {
    if (user && id) {
      await supabase.from("idea_interactions").insert({
        user_id: user.id,
        idea_id: id,
        event_type: "view",
      });
    }
  };

  const enrichIdea = async () => {
    setEnriching(true);
    try {
      const { data, error } = await supabase.functions.invoke("enrich-startup", {
        body: { ideaId: id },
      });

      if (error) throw error;

      setIdea((prev) => ({
        ...prev!,
        deep_description: data.deepDescription,
        build_steps: data.buildSteps,
      }));

      toast({
        title: "Success",
        description: "Startup analysis complete!",
      });
    } catch (error) {
      console.error("Error enriching idea:", error);
      toast({
        title: "Error",
        description: "Failed to generate analysis",
        variant: "destructive",
      });
    } finally {
      setEnriching(false);
    }
  };

  const saveToMyIdeas = async () => {
    if (!user || !idea) return;

    try {
      const { data: newIdea, error } = await supabase.from("ideas").insert({
        user_id: user.id,
        title: idea.title,
        tagline: idea.tagline,
        description: idea.description,
        problem: idea.deep_description?.problem || "",
        solution: idea.deep_description?.solution || "",
        icp: idea.deep_description?.target_audience || "",
        niche: idea.tags?.[0] || "General",
        tags: idea.tags,
      }).select().single();

      if (error) throw error;

      if (newIdea) {
        await supabase.functions.invoke('generate-idea-report', {
          body: {
            ideaId: newIdea.id,
            title: newIdea.title,
            description: newIdea.description || newIdea.title,
            niche: newIdea.niche,
            tags: newIdea.tags || [],
          }
        });
      }

      await supabase.from("idea_interactions").insert({
        user_id: user.id,
        idea_id: idea.id,
        event_type: "favorite",
      });

      setIsSaved(true);
      toast({
        title: "Saved!",
        description: "Startup added to your dashboard",
      });
    } catch (error) {
      console.error("Error saving idea:", error);
      toast({
        title: "Error",
        description: "Failed to save startup",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-12 w-3/4" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
            <div>
              <Skeleton className="h-80 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!idea) {
    return (
      <div className="container py-8">
        <Alert>
          <AlertDescription>Startup not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  const metrics = getIdeaMetrics(idea);
  const whyThisWins = getWhyThisWins(idea);
  const maxPopularity = Math.max(...trendData.map((d) => d.popularity_index), 1);

  return (
    <div className="min-h-screen bg-background">
      <div className="container py-8">
        {/* Back Button */}
        <Button variant="ghost" asChild className="mb-6 hover:bg-accent/50">
          <Link to="/startups">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Ideas
          </Link>
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
                {idea.title}
              </h1>
              {idea.tagline && (
                <p className="text-lg text-muted-foreground mb-4">
                  {idea.tagline}
                </p>
              )}
              {idea.tags && idea.tags.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {idea.tags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="text-sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Idea Signal Section */}
            <Card className="border-border/60 shadow-soft">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="h-5 w-5 text-primary" />
                  Idea Signal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 rounded-xl bg-primary/5 border border-primary/10">
                    <div className="text-3xl font-bold text-primary mb-1">{metrics.ideaScore}</div>
                    <div className="text-sm text-muted-foreground">Idea Score</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-accent/50">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Target className="h-5 w-5 text-foreground/70" />
                      <span className="text-lg font-semibold">{metrics.difficulty}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">Difficulty</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-accent/50">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Clock className="h-5 w-5 text-foreground/70" />
                      <span className="text-lg font-semibold">{metrics.timeToMVP}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">Time to MVP</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-accent/50">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <DollarSign className="h-5 w-5 text-foreground/70" />
                      <span className="text-lg font-semibold">{metrics.monetization}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">Monetization</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Why This Wins */}
            <Card className="border-border/60 shadow-soft">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Why This Wins
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {whyThisWins.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Interest Over Time */}
            <Card className="border-border/60 shadow-soft">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Interest Over Time
                </CardTitle>
                <CardDescription>
                  Based on mentions in trending tech news and events (90 days)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis domain={[0, maxPopularity]} tick={{ fontSize: 12 }} />
                    <Tooltip
                      labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      formatter={(value: number) => [`${value} mentions`, "Popularity"]}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="popularity_index"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Deep Description or Generate Analysis */}
            {!idea.deep_description ? (
              <Card className="border-border/60 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Lightbulb className="h-5 w-5 text-primary" />
                    Deep Analysis
                  </CardTitle>
                  <CardDescription>
                    Get AI-powered insights about this startup opportunity
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={enrichIdea} disabled={enriching} size="lg">
                    {enriching ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate Analysis
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* The Problem */}
                <Card className="border-border/60 shadow-soft">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">The Problem</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/80 leading-relaxed">{idea.deep_description.problem}</p>
                  </CardContent>
                </Card>

                {/* The Solution */}
                <Card className="border-border/60 shadow-soft">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">The Solution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/80 leading-relaxed">{idea.deep_description.solution}</p>
                  </CardContent>
                </Card>

                {/* Target Audience */}
                <Card className="border-border/60 shadow-soft">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-5 w-5 text-primary" />
                      Target Audience
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/80 leading-relaxed">{idea.deep_description.target_audience}</p>
                  </CardContent>
                </Card>

                {/* Why Now? */}
                <Card className="border-border/60 shadow-soft">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Clock className="h-5 w-5 text-primary" />
                      Why Now?
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/80 leading-relaxed">{idea.deep_description.timing}</p>
                  </CardContent>
                </Card>

                {/* Competitive Landscape */}
                <Card className="border-border/60 shadow-soft">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Competitive Landscape
                    </CardTitle>
                    <CardDescription>
                      Alternatives and how this idea differentiates
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/80 leading-relaxed">{idea.deep_description.competition}</p>
                  </CardContent>
                </Card>

                {/* Risks & Constraints */}
                <Card className="border-border/60 shadow-soft">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Shield className="h-5 w-5 text-primary" />
                      Risks & Constraints
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/80 leading-relaxed">{idea.deep_description.risks}</p>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Build Roadmap */}
            {idea.build_steps && (
              <Card className="border-border/60 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Rocket className="h-5 w-5 text-primary" />
                    Build Roadmap
                  </CardTitle>
                  <CardDescription>
                    Your path from idea to launch
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {idea.build_steps.phases.map((phase) => (
                    <Collapsible key={phase.phase_number} defaultOpen={phase.phase_number === 1}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-4 rounded-xl bg-accent/50 hover:bg-accent transition-colors text-left">
                        <div>
                          <h3 className="font-semibold">
                            Phase {phase.phase_number}: {phase.phase_name}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {phase.description}
                          </p>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-4 pl-4">
                        <ol className="space-y-3">
                          {phase.steps.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              {user && (
                                <Checkbox className="mt-1" />
                              )}
                              <div className="flex-1">
                                <div className="font-medium text-foreground/90">{step.title}</div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  {step.description}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ol>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-4">
              {/* Action Card */}
              <Card className="border-border/60 shadow-soft overflow-hidden">
                <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
                      <Rocket className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Ready to create?</h3>
                    <p className="text-sm text-muted-foreground">
                      Turn this idea into a working app in minutes
                    </p>
                  </div>

                  <Button
                    onClick={() => id && createProjectFromIdea(id)}
                    disabled={isCreating}
                    className="w-full h-12 text-base font-medium bg-primary hover:bg-primary/90"
                    size="lg"
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {progress || "Creating..."}
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-5 w-5" />
                        Create with AI
                      </>
                    )}
                  </Button>

                  <div className="mt-4 flex gap-2">
                    {!isOwner && user && (
                      <Button
                        onClick={saveToMyIdeas}
                        disabled={isSaved}
                        variant="outline"
                        className="flex-1"
                      >
                        <Heart className={`mr-2 h-4 w-4 ${isSaved ? "fill-current text-red-500" : ""}`} />
                        {isSaved ? "Saved" : "Save"}
                      </Button>
                    )}
                    {isOwner && (
                      <Button asChild variant="outline" className="flex-1">
                        <Link to="/dashboard">
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          Dashboard
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="border-t border-border/60 p-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Idea Score</span>
                    <span className="font-semibold text-primary">{metrics.ideaScore}/100</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Difficulty</span>
                    <Badge variant={metrics.difficulty === "Easy" ? "secondary" : metrics.difficulty === "Medium" ? "outline" : "destructive"} className="font-medium">
                      {metrics.difficulty}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Time to MVP</span>
                    <span className="font-medium">{metrics.timeToMVP}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Monetization</span>
                    <span className="font-medium text-green-600">{metrics.monetization}</span>
                  </div>
                </div>
              </Card>

              {/* Tags Card */}
              {idea.tags && idea.tags.length > 0 && (
                <Card className="border-border/60 shadow-soft p-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {idea.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartupDetail;
